import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { SliceData } from '../common/slicemesh/SliceData';
import { Contour } from './Contour';

/**
 * {@link ContourSlice} is {@link SliceData} that contains a collection of {@link Contour}s within.\
 * All of those {@link Contour}s share the same {@link SliceData.normalPosition normalPosition}.
 */
export class ContourSlice extends SliceData {
    /**
     * The threshold for the spacing between two slices to be considered equal. TODO: could be a factor of the slice thickness.
     */
    public static SLICE_SPACING_THRESHOLD = 0.1;

    public static isSameSlice(pos1: number, pos2: number): boolean {
        return Math.abs(pos1 - pos2) < ContourSlice.SLICE_SPACING_THRESHOLD;
    }

    /**
     * {@link Array} of {@link Contour}s that this {@link ContourSlice} contains.
     */
    private _contours: Contour[];

    /**
     * The total number of vertices in all the contours within this {@link ContourSlice}.
     */
    private _vertexCount: number;

    /**
     * {@link vec3 Coordinates} of the centroid point of this {@link ContourSlice}.\
     * This is the average computed from all the {@link Contour.vertices vertices} of all the contours within this {@link ContourSlice}.\
     * Undefined if the {@link _centroid} has not yet been computed.
     */
    private _centroid?: vec3;

    /**
     * Method to create a new {@link ContourSlice} at the specified {@link normalPosition} and array of contours.
     * @param normalPosition The {@link SliceData.normalPosition normalPosition} at which this {@link ContourSlice} resides.
     * @param contours {@link Array} of {@link Contour}s that populate this {@link ContourSlice}.
     */
    public constructor(normalPosition: number, contours: Contour[]) {
        super(normalPosition);
        this._contours = contours;
        this._vertexCount = 0;
        for (const contour of contours) {
            this._vertexCount += contour.vertexCount;
        }
    }

    public set contours(contours: Contour[]) {
        this._contours = contours;
        this._vertexCount = 0;
        for (const contour of contours) {
            this._vertexCount += contour.vertexCount;
        }
        this._centroid = undefined;
    }

    public get contours(): Contour[] {
        return this._contours;
    }

    /**
     * Method to get the total number of vertices in all the contours within this {@link ContourSlice}.
     */
    public get vertexCount(): number {
        return this._vertexCount;
    }

    /**
     * Method to get the total number of edges amongst all the contours within this {@link ContourSlice}.
     */
    public get edgeCount(): number {
        let edges = 0;
        for (const contour of this.contours) {
            edges += contour.edgeCount;
        }
        return edges;
    }

    /**
     * Method to get the number of contours within this {@link ContourSlice}.\
     * Note: Holes within {@link Contour}s are also stored as separate {@link Contour}s.
     */
    public get numberOfContours(): number {
        return this._contours.length;
    }

    /**
     * Method to get the {@link _centroid} of this {@link ContourSlice}.\
     * {@link _centroid} is the average computed from all the {@link Contour.vertices vertices} of all the contours within this {@link ContourSlice}.\
     * If {@link _centroid} is undefined, it will be re-computed. And it will stay constant unless new {@link Contour}s are {@link addContour added}.
     */
    public get centroid(): ReadonlyVec3 {
        if (!this._centroid) {
            this._centroid = vec3.create();
            for (const contour of this.contours) {
                vec3.scaleAndAdd(
                    this._centroid,
                    this._centroid,
                    contour.centroid,
                    contour.vertexCount / this._vertexCount
                );
            }
        }
        return this._centroid;
    }

    /**
     * Method to add a new {@link Contour} to this {@link ContourSlice}.\
     * This increases the {@link _vertexCount} and nullifies the {@link _centroid} as it is no more up-to-date.
     * @param contour The {@link Contour} that is to be added.
     * @returns Reference to this
     */
    public addContour(contour: Contour): ContourSlice {
        this._contours.push(contour);
        this._vertexCount += contour.vertexCount;
        this._centroid = undefined;
        return this;
    }

    public getContour(index: number): Contour | undefined {
        return index < this._contours.length
            ? this._contours[index]
            : undefined;
    }
}
